import { Alert, Button } from "@simplicity/ui";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router";
import {
  isPaused,
  pollNotice,
  setupOutcome,
  setupStartUrl,
  step0Summary,
  type Step0Summary,
} from "./agentSetupModel";
import { RunStatusBadge } from "./OnsiteBadges";
import { formatDateTime } from "./onsiteModel";
import type { OnsiteWorkspace } from "./types";

const PAUSED_NOTICE =
  "This client is paused, so its agent checks in once a day. Queued plans and SFTP changes run at the next check-in, or right away from Cloud Run → simplicity-agent → Execute in the client's project.";
const SLOW_NOTICE =
  "This client is paused, but its agent still checks in every 5 minutes. Run setup again to slow it to once a day.";
const RESTORE_NOTICE =
  "This client is active again, but its agent still checks in once a day. Run setup again to restore 5-minute check-ins.";

function PollNotice({ workspace }: { workspace: OnsiteWorkspace }) {
  const notice = pollNotice(workspace);
  if (notice === "paused") return <Alert variant="info">{PAUSED_NOTICE}</Alert>;
  if (notice === "slow") return <Alert variant="warning">{SLOW_NOTICE}</Alert>;
  if (notice === "restore") return <Alert variant="warning">{RESTORE_NOTICE}</Alert>;
  return null;
}

function SetupBanner({ daily }: { daily: boolean }) {
  const [searchParams] = useSearchParams();
  const outcome = setupOutcome(searchParams.get("setup"));
  if (outcome === "ok") {
    return (
      <Alert variant="success">
        {daily
          ? "Setup finished. The agent checks in at its next daily check-in."
          : "Setup finished. The agent checks in on its next poll, within 5 minutes."}
      </Alert>
    );
  }
  if (outcome === "failed") {
    return (
      <Alert variant="destructive" role="alert">
        Setup failed. The last result below says at which step, and why.
      </Alert>
    );
  }
  return null;
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="contents">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-all text-foreground">{children}</dd>
    </div>
  );
}

function LastSetup({ summary }: { summary: Step0Summary }) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-foreground">Last setup</h4>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
        <Fact label="When">{formatDateTime(summary.ranAt)}</Fact>
        <Fact label="By">{summary.ranBy ?? "—"}</Fact>
        <Fact label="Consented as">{summary.consentedAs ?? "—"}</Fact>
        <Fact label="Result">
          <RunStatusBadge status={summary.status} />
        </Fact>
        {summary.failedStep && <Fact label="Failed at">{summary.failedStep}</Fact>}
      </dl>
      {summary.message && <p className="break-words text-sm text-foreground">{summary.message}</p>}
      {summary.steps.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">
            {summary.steps.length} steps
          </summary>
          <ul className="mt-2 space-y-1 font-mono text-xs" aria-label="Setup steps">
            {summary.steps.map((s, i) => (
              <li key={`${i}:${s.step}`} className="break-all">
                {s.step} · {s.status} · {s.detail}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** Runs onsite Step 0 in the client's project with a one-time consent from the client's account. */
export function AgentSetupPanel({ workspace }: { workspace: OnsiteWorkspace }) {
  const summary = step0Summary(workspace);
  const label = summary ? "Run setup again" : "Set up agent";
  return (
    <section className="space-y-3" aria-label="Agent setup">
      <h3 className="text-lg text-foreground">Agent setup</h3>
      <PollNotice workspace={workspace} />
      <SetupBanner daily={isPaused(workspace)} />
      <p className="text-sm text-muted-foreground">
        The client&apos;s Simplicity Google account consents on Google&apos;s screen; it must be an
        Owner of {workspace.gcpProjectId}. Its access is used for this one setup and not kept.
      </p>
      {workspace.status === "disabled" ? (
        <Button disabled>{label}</Button>
      ) : (
        <Button asChild>
          <a href={setupStartUrl(workspace.id)}>{label}</a>
        </Button>
      )}
      {summary && <LastSetup summary={summary} />}
    </section>
  );
}
